import React, { useState, useMemo, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { Flag, Check } from 'phosphor-react';
import { reportSoal, reportVocabDrill } from '../../../services/reportApi';
import { reportedSoalState } from '../../../recoil/atoms';

const ReportSoalButton = ({
  type,
  prosetId,
  soalId = null,
  drillId = null,
  onSuccess,
  onError,
  allowReReport = false,
  persistReportedState = false,
  reportText = 'Report',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReportedLocal, setIsReportedLocal] = useState(false);
  const [reportedSoalSet, setReportedSoalSet] = useRecoilState(reportedSoalState);

  const isVocabDrill = Boolean(drillId);

  const reportKey = useMemo(() => {
    if (isVocabDrill) {
      return `reported-drill-${drillId}-${soalId || 'no-soal-id'}`;
    }
    if (!prosetId) return null;
    return `reported-soal-${type}-${prosetId}-${soalId || 'no-soal-id'}`;
  }, [type, prosetId, soalId, drillId, isVocabDrill]);

  useEffect(() => {
    if (persistReportedState) {
      return;
    }
    setIsReportedLocal(false);
  }, [reportKey, persistReportedState]);

  const isReported = useMemo(() => {
    if (persistReportedState && reportKey) {
      return reportedSoalSet.has(reportKey);
    }
    return isReportedLocal;
  }, [persistReportedState, reportKey, reportedSoalSet, isReportedLocal]);

  const handleReport = async () => {
    if (isVocabDrill) {
      if (!drillId) return;
    } else {
      if (!type || !prosetId) return;
    }

    setIsSubmitting(true);
    try {
      const result = isVocabDrill
        ? await reportVocabDrill(drillId, soalId)
        : await reportSoal(type, prosetId, soalId);
      if (result.success) {
        setIsReportedLocal(true);
        if (reportKey) {
          setReportedSoalSet(prev => new Set([...prev, reportKey]));
        }
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        if (onError) {
          onError(result.message || 'Failed to submit report');
        } else {
          console.error('ReportSoalButton: Report failed', result.message);
        }
      }
    } catch (error) {
      if (onError) {
        onError(error.message || 'Failed to submit report');
      } else {
        console.error('ReportSoalButton: Report error', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Always render button, but disable only if required props are missing or submitting
  const isDisabled = isSubmitting || (isVocabDrill ? !drillId : (!type || !prosetId));

  return (
    <button
      className={`report-soal-button inline-flex items-center gap-2 max-md:gap-1 p-[8px_12px] max-md:p-[6px_10px] border rounded-[8px] cursor-pointer transition-all duration-200 font-[inherit] hover:border-[var(--text-tertiary)] hover:bg-[var(--bg-hover)] active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed ${isReported ? 'bg-[#f0fdf4] border-[#86efac] dark:bg-[rgba(34,197,94,0.1)] dark:border-[rgba(134,239,172,0.3)] hover:border-[#4ade80] hover:bg-[#dcfce7]' : 'bg-[var(--card-bg)] border-[var(--input-border)]'}`}
      onClick={handleReport}
      disabled={isDisabled}
      aria-label={isReported ? 'Reported' : reportText}
    >
      {isReported ? (
        <>
          <Check size={16} weight="regular" className="flex-shrink-0 text-[#16a34a]" />
          <span className="text-[14px] max-md:text-[12px] max-md:whitespace-nowrap font-normal leading-[1.5] text-[#16a34a]">Reported</span>
        </>
      ) : (
        <>
          <Flag size={16} weight="regular" className="flex-shrink-0 text-[var(--text-secondary)]" />
          <span className="text-[14px] max-md:text-[12px] max-md:whitespace-nowrap font-normal leading-[1.5] text-[var(--text-secondary)]">{reportText}</span>
        </>
      )}
    </button>
  );
};

export default ReportSoalButton;
